<script lang="ts">
  import { onMount, onDestroy } from "svelte";

  /**
   * Props interface for AutoScroll component
   */
  export let speed: number = 50; // Pixels per second
  export let direction: "left" | "right" | "up" | "down" = "left";
  export let pauseOnHover: boolean = true;
  export let className: string = "";
  export let gap: number = 0;
  export let itemsToShow: number | null = null;
  export let showPauseButton: boolean = false;

  let scrollContent: HTMLElement;
  let scrollWrapper: HTMLElement;
  let animationFrameId: number | null = null;
  let isPaused: boolean = false;
  let isHovered: boolean = false;
  let contentWidth: number = 0;
  let contentHeight: number = 0;
  let currentPosition: number = 0;
  let lastTimestamp: number = 0;
  let itemWidth: number = 0;
  let itemHeight: number = 0;

  let isDestroyed: boolean = false;

  let viewportWidth: number = 0;
  const mdBreakpoint: number = 768;
  $: isMobile = viewportWidth < mdBreakpoint;

  function duplicateContent(): void {
    if (!scrollContent || isDestroyed) return;

    const children = Array.from(scrollContent.children);
    children.forEach(child => {
      const clone = child.cloneNode(true) as HTMLElement;
      scrollContent.appendChild(clone);
    });
  }

  function updateContentDimensions(): void {
    if (!scrollContent || isDestroyed) return;

    const children = scrollContent.children;

    if (children.length > 0) {
      const halfCount = Math.floor(children.length / 2);
      let width = 0;
      let height = 0;

      const firstChild = children[0] as HTMLElement;
      itemWidth = firstChild.offsetWidth;
      itemHeight = firstChild.offsetHeight;

      for (let i = 0; i < halfCount; i++) {
        const child = children[i] as HTMLElement;
        if (direction === "left" || direction === "right") {
          width += child.offsetWidth + (i < halfCount - 1 ? gap : 0);
        } else {
          height += child.offsetHeight + (i < halfCount - 1 ? gap : 0);
        }
      }

      contentWidth = width;
      contentHeight = height;

      updateWrapperDimensions();
    }
  }

  function animate(timestamp: number): void {
    if (isDestroyed) return;

    if (!lastTimestamp) {
      lastTimestamp = timestamp;
    }

    const deltaTime = timestamp - lastTimestamp;
    lastTimestamp = timestamp;

    const shouldPause = isPaused || (!isMobile && isHovered);

    if (!shouldPause && scrollContent) {
      const pixelsToMove = (speed * deltaTime) / 1000;

      switch (direction) {
        case "left":
          currentPosition -= pixelsToMove;
          if (currentPosition <= -contentWidth) {
            currentPosition += contentWidth;
          }
          scrollContent.style.transform = `translate3d(${currentPosition}px, 0, 0)`;
          break;

        case "right":
          currentPosition += pixelsToMove;
          if (currentPosition >= contentWidth) {
            currentPosition -= contentWidth;
          }
          scrollContent.style.transform = `translate3d(${currentPosition}px, 0, 0)`;
          break;

        case "up":
          currentPosition -= pixelsToMove;
          if (currentPosition <= -contentHeight) {
            currentPosition += contentHeight;
          }
          scrollContent.style.transform = `translate3d(0, ${currentPosition}px, 0)`;
          break;

        case "down":
          currentPosition += pixelsToMove;
          if (currentPosition >= contentHeight) {
            currentPosition -= contentHeight;
          }
          scrollContent.style.transform = `translate3d(0, ${currentPosition}px, 0)`;
          break;
      }
    }

    if (!isDestroyed) {
      animationFrameId = requestAnimationFrame(animate);
    }
  }

  function startAnimation(): void {
    if (isDestroyed || animationFrameId !== null) {
      return;
    }

    lastTimestamp = 0;
    animationFrameId = requestAnimationFrame(animate);
  }

  function stopAnimation(): void {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
      lastTimestamp = 0;
    }
  }

  function handleMouseEnter(): void {
    if (pauseOnHover && !isMobile) {
      isHovered = true;
    }
  }

  function handleMouseLeave(): void {
    if (!isMobile) {
      isHovered = false;
    }
  }

  export function togglePause(): void {
    isPaused = !isPaused;
  }

  export function resume(): void {
    isPaused = false;
  }

  export function pause(): void {
    isPaused = true;
  }

  function handleVisibilityChange(): void {
    if (isDestroyed) return;

    if (document.hidden) {
      stopAnimation();
    } else {
      startAnimation();
    }
  }

  onMount(() => {
    setTimeout(() => {
      duplicateContent();
      updateWrapperDimensions();
      updateContentDimensions();
      startAnimation();
    }, 100);

    const resizeObserver = new ResizeObserver(() => {
      if (!isDestroyed) {
        updateWrapperDimensions();
        updateContentDimensions();
      }
    });

    if (scrollContent) {
      resizeObserver.observe(scrollContent);
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      resizeObserver.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  });

  onDestroy(() => {
    isDestroyed = true;
    stopAnimation();
  });

  function updateWrapperDimensions(): void {
    if (!scrollWrapper || !itemsToShow || isDestroyed) return;

    scrollWrapper.style.width = "auto";

    const parentWidth = scrollWrapper.offsetWidth;

    if (direction === "left" || direction === "right") {
      const calculatedItemWidth =
        (parentWidth - gap * (itemsToShow - 1)) / itemsToShow;

      const children = scrollContent?.children;
      if (children) {
        for (let i = 0; i < children.length; i++) {
          const child = children[i] as HTMLElement;
          child.style.width = `${calculatedItemWidth}px`;
        }
      }
    }
  }

  let prevSpeed = speed;
  $: if (speed !== prevSpeed && !isDestroyed) {
    prevSpeed = speed;
    if (animationFrameId !== null) {
      stopAnimation();
      startAnimation();
    }
  }

  $: if (itemsToShow && itemWidth && scrollWrapper && !isDestroyed) {
    updateWrapperDimensions();
  }
</script>

<style lang="scss">
  .auto-scroll {
    position: relative;
    width: 100%;
    height: auto;
    overflow: hidden;
  }

  .auto-scroll__pause-button {
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    z-index: 100;
    background: rgba(0, 0, 0, 0.6);
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 2rem;
    cursor: pointer;
    transition: background 0.2s ease;
    color: white;
    font-size: 0.875rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .auto-scroll__pause-button:hover {
    background: rgba(0, 0, 0, 0.8);
  }

  .auto-scroll__pause-button:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }

  /* Only show on mobile when enabled */
  @include breakpoint(md) {
    .auto-scroll__pause-button {
      display: none;
    }
  }

  .auto-scroll__pause-icon {
    font-size: 1rem;
  }

  .auto-scroll__wrapper {
    position: relative;
    width: 100%;
    height: auto;
    overflow: hidden;
  }

  .auto-scroll__container {
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
  }

  .auto-scroll__container--horizontal {
    flex-direction: row;
  }

  .auto-scroll__container--vertical {
    flex-direction: column;
  }

  .auto-scroll__content {
    display: inline-flex;
    flex-shrink: 0;
    position: relative;
    will-change: transform;
    transform: translate3d(0, 0, 0);
    /* Ensure content doesn't wrap */
    white-space: nowrap;
  }

  .auto-scroll__content--horizontal {
    flex-direction: row;
  }

  .auto-scroll__content--vertical {
    flex-direction: column;
  }

  .auto-scroll__content :global(> *) {
    flex-shrink: 0;
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div
  class="auto-scroll {className}"
  on:mouseenter={handleMouseEnter}
  on:mouseleave={handleMouseLeave}
  role="region"
  aria-label="Auto-scrolling content"
>
  <div class="auto-scroll__wrapper" bind:this={scrollWrapper}>
    <div
      class="auto-scroll__container auto-scroll__container--{direction ===
        'left' || direction === 'right'
        ? 'horizontal'
        : 'vertical'}"
    >
      <div
        class="auto-scroll__content auto-scroll__content--{direction ===
          'left' || direction === 'right'
          ? 'horizontal'
          : 'vertical'}"
        bind:this={scrollContent}
        style="gap: {gap}px"
      >
        <slot />
      </div>
    </div>
  </div>

  {#if showPauseButton && isMobile}
    <button
      class="auto-scroll__pause-button"
      on:click={togglePause}
      aria-label={isPaused ? "Resume auto-scroll" : "Pause auto-scroll"}
      type="button"
    >
      <span>{isPaused ? "Resume" : "Pause"}</span>
      <span class="auto-scroll__pause-icon" aria-hidden="true">
        {isPaused ? "▶" : "❚❚"}
      </span>
    </button>
  {/if}
</div>
